import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/wellness-coach'

export const wellnessCoachTutorial: Tutorial = {
  slug: 'wellness-coach',
  title: 'Wellness Coach',
  subtitle: 'Genetics-aware AI chat that already knows your biology',
  date: '2026-07-09',
  category: 'Core Features',
  platform: 'mobile',
  tags: ['Wellness Coach', 'genetics', 'AI', 'MTHFR', 'suggested goals'],
  description:
    'Livewello\'s Wellness Coach grounds every answer in your actual genetic variants, health records, and lab results — then hands you a concrete next step when a habit is worth tracking.',
  highlights: [
    'Starter topics and a guided welcome — no blank box on first open.',
    'Answers cite your real variants (e.g. MTHFR rs1801131), not generic folate advice.',
    'Suggested goal cards bridge insight straight into Wellness Goals in one tap.',
  ],
  coverImage: `${IMG}/01_welcome_and_starters.png`,
  sections: [
    {
      heading: 'Starting a conversation',
      paragraphs: [
        'Open Wellness Coach and there\'s no empty chat staring back. The Coach introduces itself and offers a **Choose a topic** shortcut for people who aren\'t sure what to ask yet.',
      ],
      image: {
        src: `${IMG}/01_welcome_and_starters.png`,
        alt: 'Wellness Coach welcome message with starter topic shortcuts',
        caption: 'Welcome message and starter topics',
      },
    },
    {
      heading: 'Asking a real question',
      paragraphs: [
        'Type a question — for example, "What does my MTHFR gene mean for my folate needs?" — and send it. The message posts immediately and the Coach shows a "Preparing your answer..." indicator while it works.',
        'Under the hood this calls `POST /llm/stream`, which pulls your genetic report and health record data into context before generating a response.',
      ],
      image: {
        src: `${IMG}/02_question_sent.png`,
        alt: 'User question sent with Coach preparing an answer',
        caption: 'Question sent, Coach preparing an answer',
      },
    },
    {
      heading: 'A genetics-grounded answer',
      paragraphs: [
        'The response isn\'t generic folate advice — it names your actual variant and cites the relevant biology.',
        'Every response carries a standing disclaimer — this is educational information, not medical advice — and closes by checking whether you want to go deeper.',
      ],
      quote:
        'Based on your MTHFR A1298C variant (rs1801131)… Methylfolate supports the production of SAM-e, which in turn supports serotonin, dopamine, and norepinephrine. Folate and B12 work together in the methylation cycle.',
      image: {
        src: `${IMG}/03_genetics_answer_and_suggested_goal.png`,
        alt: 'Genetics-specific Coach answer with suggested goal card',
        caption: 'Genetics-specific answer with a suggested goal',
      },
    },
    {
      heading: 'From answer to action: suggested goals',
      paragraphs: [
        'The Coach doesn\'t stop at information. When a genetic insight suggests a concrete habit, it surfaces a **Suggested goal** card right in the conversation — pre-filled with a title and a reason tied to the specific variant discussed.',
        'Tapping **Add goal** creates it directly in Wellness Goals — no re-typing, no leaving the chat. This is the bridge between the Coach (understanding) and Goals (doing).',
      ],
      quote:
        'Increase folate-rich foods daily — Based on your MTHFR A1298C variant (rs1801131), regularly eating leafy greens, lentils, and avocado can support your methylation pathway naturally.',
      image: {
        src: `${IMG}/04_suggested_goal_card.png`,
        alt: 'Suggested goal card with Add goal button in Wellness Coach',
        caption: 'Suggested goal card with Add goal button',
      },
    },
  ],
  whyMatters:
    'Most health chatbots either know nothing about the person asking, or force them to re-explain their history every conversation. Livewello\'s Coach already knows your genetics and records, so every answer starts from your actual biology — and it doesn\'t just inform, it hands you a concrete next step.',
  nextSlug: 'wellness-goals',
  relatedSlugs: ['wellness-goals', 'health-records'],
  meta: {
    description:
      'Walk through Livewello\'s genetics-aware Wellness Coach: starter topics, variant-specific answers, and one-tap suggested goals.',
    ogImage: `${IMG}/01_welcome_and_starters.png`,
  },
}
